import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Container, Section, Eyebrow, DisplayHeading, BodyCopy, Button, Reveal, Badge, StatusBadge, TextLink } from "@/components/kit";
import { MaskedLinesInView } from "@/components/MaskedLines";
import { ScoreBar } from "@/components/TrustScore";
import RegulationTrace from "@/components/RegulationTrace";
import { CTASection } from "@/components/shared";
import { color } from "@/lib/theme";
import { fadeUp, stagger, viewport } from "@/lib/motion";

/* ---------- Hero governance state ---------- */
function GovernanceState() {
  const rows = [
    ["AI systems", "12", "estate"],
    ["Ownership", "11 / 12", "1 unassigned"],
    ["Open risks", "7", "2 high"],
    ["Controls", "43", "active"],
  ];
  const trace = [
    ["AI System", "Customer Support Agent", "brand"],
    ["Risk", "Unauthorized Output", "danger"],
    ["Control", "Human Oversight", "brand-deep"],
    ["Owner", "AI Governance", "brand"],
    ["Evidence", "EV-DEMO-0284", "gold"],
  ];
  return (
    <div className="relative mx-auto w-full max-w-[540px]">
      <div className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full bg-brand/[0.075] blur-3xl" />
      <motion.div initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ delay: 0.4, duration: 1, ease: [0.16, 1, 0.3, 1] }} className="relative overflow-hidden rounded-xl border border-line bg-panel-raised/70 p-5 backdrop-blur-xl sm:p-7">
        <div className="absolute inset-0 tech-grid opacity-60" />
        <div className="relative flex flex-wrap items-start justify-between gap-2 border-b border-line pb-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-content-faint">Meira Governance State</div>
            <div className="mt-1 font-mono text-[8.5px] uppercase tracking-[0.18em] text-brand-soft/80">Demo Organization · synthetic</div>
          </div>
          <StatusBadge status="finding">Action required</StatusBadge>
        </div>
        <div className="relative mt-4 grid grid-cols-2 gap-2.5">
          {rows.map(([k, v, s]) => (
            <div key={k} className="rounded-md border border-line-soft bg-panel p-3">
              <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-content-faint">{k}</div>
              <div className="mt-0.5 font-display text-xl font-light text-content">{v}</div>
              <div className="font-mono text-[9px] tracking-wide text-content-faint">{s}</div>
            </div>
          ))}
        </div>
        <div className="relative mt-4 rounded-md border border-line-soft bg-panel p-4">
          <div className="flex items-center justify-between">
            <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-content-faint">ISO/IEC 42001 readiness</span>
            <span className="font-display text-lg text-gradient-gold">78%</span>
          </div>
          <div className="mt-3"><ScoreBar label="Evidence coverage" value={82} tone="gold" /></div>
        </div>
        <div className="relative mt-4 flex flex-wrap items-center gap-x-2 gap-y-2 rounded-md border border-line-soft bg-panel p-4">
          {trace.map(([k, v, c], i) => (
            <React.Fragment key={k}>
              <span className="inline-flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: color(c) }} />
                <span className="font-mono text-[10px] text-content">{v}</span>
              </span>
              {i < trace.length - 1 && <span className="text-content-faint">→</span>}
            </React.Fragment>
          ))}
          <span className="ml-auto"><StatusBadge status="finding">Partial</StatusBadge></span>
        </div>
        <div className="relative mt-4 text-center font-mono text-[9px] uppercase tracking-[0.18em] text-content-faint">Demonstration data</div>
      </motion.div>
    </div>
  );
}

/* ---------- Lifecycle ---------- */
const LIFECYCLE = [
  ["Discover", "System registered", "Type · Conversational AI"],
  ["Classify", "Risk class assigned", "Moderate / High · Customer interaction"],
  ["Assign", "Owner assigned", "Head of Customer AI · 2nd line: AI Governance"],
  ["Assess", "Risks identified", "Unauthorized output · Data exposure · Excessive agency"],
  ["Control", "Controls mapped", "Human oversight · Access boundaries · Testing"],
  ["Evidence", "Evidence collected", "Policy · Assessment · Approval · Retest"],
  ["Assure", "Governance state", "Current"],
];

/* ---------- Object model ---------- */
const OBJECTS = [
  { id: "owner", rel: "owned by", label: "AI Product Owner", ev: "Ownership record" },
  { id: "risk", rel: "has risk", label: "Sensitive Information Disclosure", ev: "Risk RISK-DEMO-0017" },
  { id: "control", rel: "mitigated by", label: "Data Handling Control", ev: "Control CTRL-DEMO-0021" },
  { id: "evidence", rel: "evidenced by", label: "Security Assessment", ev: "Evidence EV-DEMO-0284" },
  { id: "framework", rel: "mapped to", label: "ISO/IEC 42001 requirement", ev: "REQ-DEMO-04" },
  { id: "policy", rel: "governed by", label: "AI Security Policy", ev: "Policy POL-DEMO-0007" },
];

function ObjectModel() {
  const [active, setActive] = useState("risk");
  const obj = OBJECTS.find((o) => o.id === active);
  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_1fr] lg:items-center">
      <div className="relative rounded-lg border border-line bg-panel-raised/50 p-5 tech-grid sm:p-8">
        <div className="mb-6 flex flex-col items-center sm:mb-7">
          <div className="relative flex h-24 w-24 flex-col items-center justify-center rounded-full border-2 border-brand/50 bg-ink brand-glow">
            <span className="absolute inset-0 animate-pulse-ring rounded-full bg-brand/25" />
            <span className="px-2 text-center font-mono text-[9px] uppercase leading-tight tracking-[0.14em] text-brand-soft">Customer Support Agent</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
          {OBJECTS.map((o) => (
            <button
              key={o.id}
              data-testid={`gov-object-${o.id}`}
              onMouseEnter={() => setActive(o.id)}
              onFocus={() => setActive(o.id)}
              onClick={() => setActive(o.id)}
              className={`rounded-md border p-3 text-left transition-colors duration-300 focus:outline-none ${active === o.id ? "border-brand/50 bg-brand/10" : "border-line bg-panel hover:border-brand/50"}`}
            >
              <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-content-faint">{o.rel}</div>
              <div className="mt-0.5 text-[12px] font-medium text-content">{o.label}</div>
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="rounded-lg border border-line bg-panel-raised p-5 sm:p-7">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-content-faint">Relationship</div>
          <AnimatePresence mode="wait">
            <motion.div key={active} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
              <h4 className="mt-3 font-display text-xl text-content">Customer Support Agent <span className="text-brand-soft">{obj.rel}</span> {obj.label}</h4>
              <div className="mt-4 inline-flex rounded-full border border-line bg-panel-raised px-3 py-1 font-mono text-[10px] tracking-wide text-content-muted">{obj.ev}</div>
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="mt-4 rounded-lg border border-line bg-panel-raised/50 p-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand">Every risk should have</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {["An owner", "A control", "Evidence", "A status", "An action"].map((x) => (
              <span key={x} className="rounded-full border border-line bg-panel-raised px-3 py-1 text-[13px] text-content-muted">{x}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Framework readiness ---------- */
const REQS = [
  { id: "REQ-DEMO-01", name: "AI policy", status: "Met", tone: "text-valid", evidence: [["Policy record", "Available"], ["Approval", "Recorded"]], owner: "AI Governance Lead", gap: "—", action: "Maintain review cadence." },
  { id: "REQ-DEMO-02", name: "AI system impact assessment", status: "Met", tone: "text-valid", evidence: [["Risk assessment", "Uploaded"], ["Model inventory", "Available"]], owner: "AI Governance Lead", gap: "—", action: "Keep assessments current." },
  { id: "REQ-DEMO-03", name: "Data quality governance", status: "Partial", tone: "text-warn", evidence: [["Data policy", "Available"], ["Quality checks", "Partial"]], owner: "Data Owner", gap: "Ongoing quality evidence incomplete.", action: "Record recurring data-quality checks." },
  { id: "REQ-DEMO-04", name: "Incident & corrective action", status: "Partial", tone: "text-warn", evidence: [["Process", "Defined"], ["Drill record", "Missing"]], owner: "AI Security", gap: "No exercised incident evidence.", action: "Run and record an incident exercise." },
  { id: "REQ-DEMO-05", name: "Third-party AI oversight", status: "Gap", tone: "text-danger", evidence: [["Vendor register", "Partial"], ["Assessment", "Missing"]], owner: "Procurement + AI Gov", gap: "Vendor AI not fully assessed.", action: "Assess third-party AI providers." },
];

function FrameworkReadiness() {
  const [active, setActive] = useState(REQS[3].id);
  const req = REQS.find((r) => r.id === active);
  return (
    <div className="grid gap-8 lg:grid-cols-[1.1fr_1fr] lg:items-start">
      <div className="overflow-hidden rounded-lg border border-line bg-panel-raised">
        <div className="flex flex-col gap-4 border-b border-line p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-content-faint">ISO/IEC 42001 readiness</div>
            <div className="mt-2 font-display text-4xl font-light text-gradient-gold">78%</div>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-[10px] uppercase tracking-wide sm:justify-end sm:text-right">
            <span className="text-valid">Met 62%</span>
            <span className="text-warn">Partial 24%</span>
            <span className="text-danger">Gap 14%</span>
          </div>
        </div>
        <div className="p-6"><ScoreBar label="Overall readiness" value={78} tone="gold" /></div>
        <div className="divide-y divide-line-soft border-t border-line">
          {REQS.map((r) => (
            <button key={r.id} data-testid={`req-${r.id}`} onMouseEnter={() => setActive(r.id)} onFocus={() => setActive(r.id)} onClick={() => setActive(r.id)} className={`flex w-full items-center justify-between gap-3 px-5 py-3.5 text-left transition-colors focus:outline-none sm:px-6 ${active === r.id ? "bg-panel-raised" : "hover:bg-panel-raised"}`}>
              <div className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-4">
                <span className="font-mono text-[11px] text-brand-soft">{r.id}</span>
                <span className="text-sm text-content">{r.name}</span>
              </div>
              <span className={`shrink-0 font-mono text-[11px] uppercase tracking-wide ${r.tone}`}>{r.status}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-line bg-panel-raised p-5 sm:p-7 lg:sticky lg:top-28">
        <AnimatePresence mode="wait">
          <motion.div key={req.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-content-faint">{req.id}</span>
              <StatusBadge status={req.status === "Met" ? "passed" : req.status === "Partial" ? "finding" : "risk"}>{req.status}</StatusBadge>
            </div>
            <h4 className="mt-3 font-display text-xl text-content">{req.name}</h4>
            <div className="mt-5 grid gap-px overflow-hidden rounded-md border border-line bg-panel-raised sm:grid-cols-2">
              {req.evidence.map(([k, v]) => (
                <div key={k} className="bg-ink p-3">
                  <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-content-faint">{k}</div>
                  <div className="mt-1 text-[12px] text-content">{v}</div>
                </div>
              ))}
            </div>
            <div className="mt-5 flex flex-col gap-3 text-sm">
              <Row k="Owner" v={req.owner} />
              <Row k="Gap" v={req.gap} />
              <Row k="Recommended action" v={req.action} tone="brand" />
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

function Row({ k, v, tone }) {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:gap-3">
      <span className={`font-mono text-[10px] uppercase tracking-[0.16em] sm:w-36 sm:shrink-0 ${tone === "brand" ? "text-brand" : "text-content-faint"}`}>{k}</span>
      <span className="text-[14px] leading-relaxed text-content-muted">{v}</span>
    </div>
  );
}

/* ---------- Page ---------- */
export default function Governance() {
  return (
    <>
      {/* 01 HERO */}
      <section className="relative overflow-hidden pb-12 pt-28 sm:pb-16 sm:pt-36 md:pb-24 md:pt-44">
        <div className="pointer-events-none absolute inset-0 tech-grid radial-fade opacity-50" />
        <div className="pointer-events-none absolute -top-20 left-1/4 h-[280px] w-[420px] -translate-x-1/2 rounded-full bg-brand/[0.06] blur-[95px] sm:-top-32 sm:h-[480px] sm:w-[820px] sm:blur-[130px]" />
        <Container className="relative">
          <div className="grid items-center [&>*]:min-w-0 gap-10 sm:gap-14 lg:grid-cols-[1.05fr_0.95fr]">
            <div>
              <motion.div variants={fadeUp} initial="hidden" animate="show"><Eyebrow>Governance &amp; Standards</Eyebrow></motion.div>
              <h1 className="mt-6 font-display text-[31px] font-medium leading-[1.06] tracking-tight text-content sm:mt-7 sm:text-5xl md:text-6xl">
                <MaskedLinesInView lines={["Turn AI governance", "into an operating system."]} />
              </h1>
              <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3}>
                <BodyCopy className="mt-7 max-w-xl">
                  Not static documents — a living system of inventory, ownership, policies, risk,
                  controls and evidence, built for audit.
                </BodyCopy>
                <div className="mt-8 flex max-w-xs flex-col items-stretch gap-3 sm:max-w-none sm:flex-row sm:flex-wrap sm:gap-4">
                  <Button to="/contact" variant="primary" data-testid="gov-book-demo" className="uppercase tracking-[0.12em]">Book a Demo</Button>
                  <Button to="/platform" variant="outline">Explore the Platform</Button>
                </div>
              </motion.div>
            </div>
            <GovernanceState />
          </div>
        </Container>
      </section>

      {/* 02 LIFECYCLE */}
      <Section className="border-t border-line bg-panel-raised/30">
        <Container>
          <Reveal><Eyebrow>Governance lifecycle</Eyebrow></Reveal>
          <DisplayHeading className="mt-6 max-w-2xl text-3xl leading-tight sm:text-4xl">One AI system, from discovery to assurance.</DisplayHeading>
          <Reveal i={1}><BodyCopy className="mt-6 max-w-xl">Followed with a demonstration system — the Customer Support Agent.</BodyCopy></Reveal>
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewport} className="relative mt-9 flex flex-col sm:mt-12">
            <span className="absolute left-[15px] top-4 h-[calc(100%-2rem)] w-px bg-gradient-to-b from-brand/40 via-line to-valid/50" />
            {LIFECYCLE.map(([label, head, sub], i) => (
              <motion.div key={label} variants={fadeUp} className="relative flex items-start gap-5 py-3.5">
                <span className="relative z-10 mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-line-bright bg-ink font-mono text-[10px] text-brand-soft">{String(i + 1).padStart(2, "0")}</span>
                <div className="grid gap-1 md:grid-cols-[160px_1fr] md:gap-6">
                  <span className="font-display text-lg text-content">{label}</span>
                  <span className="text-[14px] leading-relaxed text-content-muted"><span className="text-content">{head}.</span> {sub}</span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </Section>

      {/* 03 OBJECT MODEL */}
      <Section className="border-t border-line">
        <Container>
          <Reveal><Eyebrow>Connected governance</Eyebrow></Reveal>
          <DisplayHeading className="mt-6 max-w-2xl text-3xl leading-tight sm:text-4xl">Governance only works when everything connects.</DisplayHeading>
          <Reveal i={1}><BodyCopy className="mt-6 max-w-2xl">Meira doesn’t store disconnected records. Every AI system links to its owner, risks, controls, evidence and framework requirements.</BodyCopy></Reveal>
          <Reveal i={1} className="mt-8 sm:mt-12"><ObjectModel /></Reveal>
        </Container>
      </Section>

      {/* 04 FRAMEWORK READINESS */}
      <Section className="border-t border-line bg-panel-raised/30">
        <Container>
          <Reveal><Eyebrow tone="brand">Framework readiness</Eyebrow></Reveal>
          <DisplayHeading className="mt-6 max-w-2xl text-3xl leading-tight sm:text-4xl">Readiness you can see, control by control.</DisplayHeading>
          <Reveal i={1}>
            <p className="mt-6 max-w-2xl text-[15px] leading-relaxed text-content-muted">
              <span className="text-content">Meira helps organizations prepare for ISO/IEC 42001 certification.</span>{" "}
              Formal certification is issued independently by an accredited certification body. Meira screens and maps evidence — it does not itself certify.
            </p>
          </Reveal>
          <Reveal i={1} className="mt-8 sm:mt-12"><FrameworkReadiness /></Reveal>

          {/* one evidence base */}
          <Reveal i={1}>
            <div className="mt-8 rounded-lg border border-line bg-panel-raised/50 p-5 sm:mt-10 sm:p-8">
              <div className="grid gap-6 sm:gap-8 md:grid-cols-[1fr_1.2fr] md:items-center">
                <div>
                  <DisplayHeading className="text-2xl leading-snug">One evidence base. <span className="text-gradient">Multiple assurance perspectives.</span></DisplayHeading>
                  <p className="mt-4 text-[14px] leading-relaxed text-content-muted">One piece of evidence — an AI risk assessment — can support mapped requirements across multiple frameworks. Meira shows mapping as relationships, not equivalence.</p>
                </div>
                <div className="relative flex items-center justify-center py-2 sm:py-4">
                  <div className="flex flex-col items-center gap-2.5 sm:gap-3">
                    <span className="rounded-full border border-brand/25 bg-panel-raised px-3 py-1 font-mono text-[10px] tracking-wide text-content-muted sm:text-[11px]">ISO/IEC 42001</span>
                    {/* the two flanking frameworks sit beside the core only when
                        there is room; below that the whole set stacks */}
                    <div className="flex flex-col items-center gap-2.5 sm:flex-row sm:gap-3">
                      <span className="rounded-full border border-brand/25 bg-panel-raised px-3 py-1 font-mono text-[10px] tracking-wide text-content-muted sm:text-[11px]">EU AI Act</span>
                      <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border-2 border-gold/50 bg-ink text-center font-mono text-[8px] uppercase leading-tight tracking-wide text-gold gold-glow">Evidence Core</span>
                      <span className="rounded-full border border-brand/25 bg-panel-raised px-3 py-1 font-mono text-[10px] tracking-wide text-content-muted sm:text-[11px]">NIST AI RMF</span>
                    </div>
                    <span className="rounded-full border border-brand/25 bg-panel-raised px-3 py-1 font-mono text-[10px] tracking-wide text-content-muted sm:text-[11px]">UAE PDPL</span>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </Container>
      </Section>

      {/* 05 SIGNATURE: REGULATION -> CONTROL */}
      <Section className="relative overflow-hidden border-t border-line bg-panel-raised/50">
        <div className="pointer-events-none absolute -right-40 top-1/4 hidden h-[520px] w-[520px] rounded-full bg-brand/[0.04] blur-[130px] sm:block" />
        <div className="pointer-events-none absolute -left-40 top-0 hidden h-[420px] w-[420px] rounded-full bg-brand/[0.05] blur-[130px] sm:block" />
        <div className="pointer-events-none absolute inset-0 tech-grid opacity-30" />
        <Container className="relative">
          <Reveal><Eyebrow tone="brand">Regulation-to-Control Autopilot</Eyebrow></Reveal>
          <DisplayHeading className="mt-6 max-w-3xl text-3xl leading-tight sm:text-4xl md:text-5xl">Know what changed. Know what it affects. Know what to do next.</DisplayHeading>
          <Reveal i={1}>
            <BodyCopy className="mt-6 max-w-2xl">
              As rules evolve, Meira is designed to trace a verified regulatory change through affected
              requirements, controls, AI systems, evidence gaps and accountable owners. Meira identifies
              impact and coordinates action — humans stay accountable for decisions.
            </BodyCopy>
          </Reveal>
          <Reveal i={1} className="mt-10 sm:mt-14"><RegulationTrace /></Reveal>
        </Container>
      </Section>

      {/* 06 OPERATIONAL GOVERNANCE */}
      <Section className="border-t border-line">
        <Container>
          <Reveal><Eyebrow>Operational governance</Eyebrow></Reveal>
          <DisplayHeading className="mt-6 max-w-2xl text-3xl leading-tight sm:text-4xl">Policies only matter when they become operational.</DisplayHeading>

          <div className="mt-8 grid gap-5 sm:mt-12 sm:gap-6 lg:grid-cols-[1fr_1fr]">
            {/* policy -> control -> evidence */}
            <Reveal>
              <div className="rounded-lg border border-line bg-panel-raised p-5 sm:p-7">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-content-faint">Policy · POL-DEMO-0007</div>
                    <h4 className="mt-2 font-display text-lg text-content">AI Security Policy · v3.2</h4>
                  </div>
                  <StatusBadge status="passed">Approved</StatusBadge>
                </div>
                <div className="mt-5 flex flex-col gap-2.5">
                  {[["Control", "Human review for high-risk output"], ["Applies to", "Support Agent · HR Screening"], ["Owner", "AI Governance Lead"], ["Evidence", "Approval · Assessment · Review log"], ["Mappings", "ISO/IEC 42001 · NIST AI RMF"]].map(([k, v]) => (
                    <div key={k} className="flex gap-3">
                      <span className="w-24 shrink-0 font-mono text-[10px] uppercase tracking-[0.14em] text-content-faint">{k}</span>
                      <span className="text-[13px] text-content-muted">{v}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-5 border-t border-line pt-4"><StatusBadge status="active">Control operating</StatusBadge></div>
              </div>
            </Reveal>

            {/* AI estate + bridge */}
            <Reveal i={1}>
              <div className="rounded-lg border border-line bg-panel-raised p-5 sm:p-7">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-content-faint">AI estate · synthetic</div>
                <div className="mt-4 flex flex-col divide-y divide-line-soft">
                  {[["Customer Support Agent", "Owner assigned", "passed", "Governed"], ["HR Screening Assistant", "Owner assigned", "finding", "Action required"], ["Internal Copilot", "Owner missing", "risk", "Review required"], ["Document Classifier", "Owner assigned", "passed", "Current"]].map(([n, s, st, lbl]) => (
                    <div key={n} className="flex items-center justify-between gap-3 py-3">
                      <div className="min-w-0">
                        <div className="text-[14px] text-content">{n}</div>
                        <div className="font-mono text-[10px] tracking-wide text-content-faint">{s}</div>
                      </div>
                      <StatusBadge status={st}>{lbl}</StatusBadge>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>

          {/* security -> governance bridge */}
          <Reveal i={1}>
            <div className="mt-6 rounded-lg border border-line bg-panel-raised/50 p-5 sm:p-7">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-3">
                {[["Security finding", "FND-DEMO-0042", "warn"], ["Risk", "Unauthorized behavior", "danger"], ["Control", "Guardrail control", "brand-deep"], ["Owner", "AI Governance", "brand"], ["Evidence", "Retest record", "gold"], ["Status", "Control effective", "valid"]].map(([k, v, c], i, arr) => (
                  <React.Fragment key={k}>
                    <span className="inline-flex flex-col rounded-md border border-line bg-panel px-3 py-2">
                      <span className="font-mono text-[8.5px] uppercase tracking-[0.14em] text-content-faint">{k}</span>
                      <span className="flex items-center gap-1.5 text-[12px] text-content"><span className="h-1.5 w-1.5 rounded-full" style={{ background: color(c) }} />{v}</span>
                    </span>
                    {i < arr.length - 1 && <span className="text-content-faint">→</span>}
                  </React.Fragment>
                ))}
              </div>
              <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                <p className="max-w-xl text-[14px] leading-relaxed text-content-muted">Meira security findings can flow directly into the governance record, connecting technical risk with accountable controls and evidence.</p>
                <TextLink to="/ai-security">Explore AI Security</TextLink>
              </div>
            </div>
          </Reveal>
        </Container>
      </Section>

      <CTASection
        eyebrow="Operationalise governance"
        title={["Policies and controls,", "turned into evidence."]}
        body="See how Meira connects AI systems, risks, controls, frameworks and evidence into one accountable governance system."
      />
    </>
  );
}
